import { dungApiThat, goiApi, goiApiKemEtag } from '../api/http';
import type { ApiError } from '../errors';
import { mayChuVaiTroNenTang } from '../../mock/vai-tro-nen-tang';
import { mockRequest, mockState } from '../../mock/transport';
import type {
  NguCanhNenTang,
  TaoVaiTroNenTang,
  TrangQuyenNenTang,
  TrangVaiTroNenTang,
  VaiTroCuaNhanSu,
  VaiTroCuaNhanSuCoPhienBan,
  VaiTroNenTang,
} from '../../modules/vai-tro-nen-tang/types';

const GOC = '/api/v1/platform-roles';

const duongDan = (id: string, phu?: string) =>
  `${GOC}/${encodeURIComponent(id)}${phu ? `/${phu}` : ''}`;

const vaiTroCuaNhanSu = (userId: string) =>
  `/api/v1/platform-staff/${encodeURIComponent(userId)}/roles`;

/** Máy chủ giả chạy **sau** nhịp chờ và các trạng thái `?mock=`: lỗi tới cùng độ trễ với lỗi thật. */
const giaLap = <T>(lam: () => T): Promise<T> => mockRequest(undefined).then(lam);

/**
 * Trang danh sách phải có mảng `items`. Thiếu là máy chủ trả sai hợp đồng (ví
 * dụ route chưa trỏ tới tenant-service mà tới một upstream khác): báo lỗi máy
 * chủ, đừng để view vỡ với câu `items is not iterable`.
 */
const kiemTrang = <T extends { items: unknown[] }>(trang: T): T => {
  if (!Array.isArray(trang?.items)) {
    throw { code: 'INTERNAL', status: 200, message: '' } satisfies ApiError;
  }
  return trang;
};

const TRANG_RONG: TrangVaiTroNenTang = { items: [], next_cursor: null };
const DANH_MUC_RONG: TrangQuyenNenTang = { items: [], next_cursor: null };

/**
 * Truy cập vai trò nền tảng, danh mục quyền nền tảng và vai trò của từng nhân
 * sự (ADR-0074), qua Gateway tới tenant-service.
 *
 * Khác `vai-tro.repository.ts` (vai trò lớp 2 của tenant, ADR-0041): đường dẫn
 * khác, service khác, và quyền ở đây **cộng dồn** với vai trò lớp 1 ở Keycloak
 * nên gán vai trò chỉ thêm quyền, không bớt (ADR-0074 mục 5).
 *
 * Mỗi hàm chọn **lúc gọi** giữa API thật và máy chủ giả (`mock/vai-tro-nen-tang.ts`).
 */
export const vaiTroNenTangRepository = {
  /**
   * `GET /api/v1/platform-roles` — một trang, sắp theo `code`.
   *
   * `?mock=filtered-empty` trả rỗng: màn lọc ở client, nên rỗng-vì-lọc hiện ra
   * khi gõ ô tìm, đúng như `vai-tro.repository`.
   */
  danhSach: ({
    limit,
    cursor,
  }: {
    limit: number;
    cursor?: string;
  }): Promise<TrangVaiTroNenTang> => {
    if (dungApiThat()) {
      return goiApi<TrangVaiTroNenTang>(GOC, { query: { limit, cursor } }).then(kiemTrang);
    }
    if (mockState() === 'filtered-empty') return mockRequest(TRANG_RONG);
    return mockRequest(mayChuVaiTroNenTang.danhSach({ limit, cursor }), TRANG_RONG);
  },

  /**
   * `GET /api/v1/platform-permissions` — danh mục quyền nền tảng (tối đa 100 mỗi trang).
   *
   * Khác danh mục quyền của tenant: danh mục này khai trong mã nguồn của
   * backend (ADR-0074 mục 2) nên không bao giờ rỗng vì "chưa cài module nào".
   */
  danhMucQuyen: ({
    limit,
    cursor,
  }: {
    limit: number;
    cursor?: string;
  }): Promise<TrangQuyenNenTang> =>
    dungApiThat()
      ? goiApi<TrangQuyenNenTang>('/api/v1/platform-permissions', {
          query: { limit, cursor },
        }).then(kiemTrang)
      : mockRequest(mayChuVaiTroNenTang.danhMucQuyen(), DANH_MUC_RONG),

  /** `POST /api/v1/platform-roles` — `khoa` là `Idempotency-Key`, mới cho mỗi lần bấm. */
  tao: (du: TaoVaiTroNenTang, khoa: string): Promise<VaiTroNenTang> =>
    dungApiThat()
      ? goiApi<VaiTroNenTang>(GOC, { method: 'POST', body: du, idempotencyKey: khoa })
      : giaLap(() => mayChuVaiTroNenTang.tao(du)),

  /** `PATCH /api/v1/platform-roles/{id}` — chỉ đổi tên; vai trò hệ thống cũng đổi được. */
  doiTen: (id: string, name: string): Promise<VaiTroNenTang> =>
    dungApiThat()
      ? goiApi<VaiTroNenTang>(duongDan(id), { method: 'PATCH', body: { name } })
      : giaLap(() => mayChuVaiTroNenTang.doiTen(id, name)),

  /** `PUT /api/v1/platform-roles/{id}/permissions` — thay cả tập quyền. */
  ganQuyen: (id: string, permissions: string[]): Promise<VaiTroNenTang> =>
    dungApiThat()
      ? goiApi<VaiTroNenTang>(duongDan(id, 'permissions'), {
          method: 'PUT',
          body: { permissions },
        })
      : giaLap(() => mayChuVaiTroNenTang.ganQuyen(id, permissions)),

  /**
   * `DELETE /api/v1/platform-roles/{id}` → `204`.
   *
   * Còn người giữ thì `409`, **không** gỡ khỏi mọi người như vai trò tenant
   * (ADR-0074 mục 7): ở nền tảng không có ai ở trên để sửa nếu gỡ nhầm.
   */
  xoa: (id: string): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(duongDan(id), { method: 'DELETE' })
      : giaLap(() => mayChuVaiTroNenTang.xoa(id)),

  /** `GET /api/v1/platform-staff/{id}/roles` kèm `ETag`. */
  vaiTroCuaNhanSu: async (userId: string): Promise<VaiTroCuaNhanSuCoPhienBan> => {
    if (!dungApiThat()) {
      return mockRequest(undefined).then(() => mayChuVaiTroNenTang.vaiTroCuaNhanSu(userId));
    }
    const { du, etag } = await goiApiKemEtag<VaiTroCuaNhanSu>(vaiTroCuaNhanSu(userId));
    return { ...du, etag };
  },

  /** `PUT /api/v1/platform-staff/{id}/roles` với `If-Match` — `412` khi người khác vừa đổi. */
  ganVaiTroChoNhanSu: async (
    userId: string,
    roleIds: string[],
    etag: string,
  ): Promise<VaiTroCuaNhanSuCoPhienBan> => {
    if (!dungApiThat()) {
      return giaLap(() => mayChuVaiTroNenTang.ganVaiTroChoNhanSu(userId, roleIds, etag));
    }
    const kq = await goiApiKemEtag<VaiTroCuaNhanSu>(vaiTroCuaNhanSu(userId), {
      method: 'PUT',
      body: { role_ids: roleIds },
      ifMatch: etag,
    });
    return { ...kq.du, etag: kq.etag };
  },

  /**
   * `GET /api/v1/platform-context` — quyền hiệu lực của người đang đăng nhập ở
   * cổng nền tảng (ADR-0074 mục 6).
   *
   * Thay chuỗi `if/else` mà `lib/auth/oidc.ts` từng dùng để đoán quyền từ token.
   * `vaiTroLop1` chỉ dùng cho máy chủ giả; API thật đọc realm role từ token.
   */
  nguCanh: (vaiTroLop1: readonly string[]): Promise<NguCanhNenTang> =>
    dungApiThat()
      ? goiApi<NguCanhNenTang>('/api/v1/platform-context')
      : mockRequest(mayChuVaiTroNenTang.nguCanh(vaiTroLop1)),
};
